import React from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { Eye, Lock, Loader2, CreditCard, Mail, LogOut } from "lucide-react";
import { UnlockIncludes } from "@/components/UnlockIncludes";
import { SUPPORT_EMAIL } from "@/components/steps/Intro";
import { PRICE_LABEL, LIST_PRICE_LABEL, DISCOUNT_LABEL } from "@/lib/pricing";

// Muro a pieno schermo mostrato quando il credito gratuito è esaurito e l'utente prova ad
// avviare un *nuovo* screening. Non è un ingresso obbligato: chi si registra oggi arriva
// all'app e produce il primo referto senza incontrarlo mai.
//
// Le voci elencate sono le stesse di UpgradeDialog (`upg_f*`) e non quelle storiche
// `pw_inc*`: i due paywall chiedono la stessa cifra e devono vendere la stessa cosa.
// `pw_badge`, `pw_lock_notice` e `pw_inc1..5` restano nei dizionari ma non sono più usati —
// i18n.test.js verifica la parità delle chiavi fra lingue, non il loro utilizzo.
export const Paywall = ({ onUnlock, loading, onLogout, email }) => {
  const { t } = useI18n();
  const INCLUDES = [t("upg_f1"), t("upg_f2"), t("upg_f3"), t("upg_f4")];
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="paywall-view">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-md bg-primary text-white flex items-center justify-center">
              <Eye className="w-5 h-5" strokeWidth={2} />
            </span>
            <span className="leading-tight">
              <span className="block text-base font-bold tracking-tight text-slate-900">VisioScreening</span>
              <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("brand_sub")}</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            {email && <span className="hidden sm:inline text-xs text-slate-400">{email}</span>}
            {onLogout && (
              <button onClick={onLogout} data-testid="paywall-logout-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            {/* Il motivo prima dell'azione: senza, il muro sembra comparire dal nulla. */}
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-800"
              data-testid="paywall-reason">
              <Lock className="w-4 h-4" /> {t("upg_title")}
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900">{t("pw_title")}</h1>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">{t("pw_desc")}</p>
            <div className="mt-8"><UnlockIncludes items={INCLUDES} /></div>
            {/* Via d'uscita: il credito si consuma al download, il muro scatta all'avvio, quindi
                esiste un caso — download fallito dopo aver speso il credito — in cui l'utente
                resta chiuso fuori senza aver ottenuto nulla. Deve poter scrivere a qualcuno. */}
            <div className="mt-8 flex justify-start" data-testid="paywall-support">
              <a href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors">
                <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={1.8} />
                <span>{t("intro_support")} <span className="font-medium underline underline-offset-2">{SUPPORT_EMAIL}</span></span>
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Lock className="w-6 h-6" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-500">{t("pw_license_label")}</p>
                <p className="text-xs text-slate-400">{t("pw_activation")}</p>
              </div>
            </div>

            <div className="mt-8 flex items-end gap-2 flex-wrap">
              <span className="text-5xl font-extrabold tracking-tighter text-slate-900" data-testid="paywall-price">{PRICE_LABEL}</span>
              {LIST_PRICE_LABEL && (
                <s className="mb-1.5 text-lg font-semibold text-slate-400" data-testid="paywall-list-price">{LIST_PRICE_LABEL}</s>
              )}
              {DISCOUNT_LABEL && (
                <span className="mb-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">{DISCOUNT_LABEL}</span>
              )}
              <span className="mb-1.5 text-sm font-medium text-slate-400">{t("pw_price_once")}</span>
            </div>

            <Button size="lg" onClick={onUnlock} disabled={loading} data-testid="paywall-unlock-button"
              className="mt-8 w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 transition-colors">
              {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t("pw_redirect")}</>)
                : (<><CreditCard className="w-5 h-5" /> {t("pw_pay_btn")}</>)}
            </Button>

            <p className="mt-4 text-center text-xs text-slate-400">{t("pw_secure")}</p>
          </div>
        </div>
      </main>
    </div>
  );
};
